// Printable documents for staff mode: the internal quote and the
// customer-supplied garment liability waiver (guide section 6D).

import { esc, usd } from "./html.js";

const PAGE_CSS = `
  body { font: 11pt/1.45 "Public Sans", "Segoe UI", Arial, sans-serif; color: #121417; margin: 0.6in; }
  h1 { font: 800 20pt/1 "Big Shoulders Text", "Arial Narrow", sans-serif; text-transform: uppercase; letter-spacing: .03em; color: #0155a3; margin: 0 0 4pt; }
  h2 { font: 700 11pt/1.2 inherit; text-transform: uppercase; letter-spacing: .06em; border-bottom: 1px solid #121417; padding-bottom: 2pt; margin: 18pt 0 6pt; }
  .strip { height: 5pt; background: linear-gradient(90deg,#00aeef 0 25%,#ec008c 25% 50%,#fff200 50% 75%,#121417 75%); margin-bottom: 14pt; }
  .meta { display: flex; justify-content: space-between; font-size: 9.5pt; color: #5d6169; }
  table { width: 100%; border-collapse: collapse; font-size: 10pt; }
  th, td { text-align: left; padding: 4pt 3pt; border-bottom: 1px solid #ddd; vertical-align: top; }
  td.n, th.n { text-align: right; font-variant-numeric: tabular-nums; }
  tfoot td { font-weight: 700; border-top: 2px solid #121417; border-bottom: 0; }
  .box { border: 1px solid #121417; padding: 8pt 10pt; font-size: 9.5pt; }
  .lines { display: grid; grid-template-columns: 1fr 1fr; gap: 22pt 30pt; margin-top: 26pt; }
  .sig { border-top: 1px solid #121417; padding-top: 3pt; font-size: 9pt; color: #5d6169; }
  .cb { display: inline-block; width: 9pt; height: 9pt; border: 1px solid #121417; margin-right: 5pt; vertical-align: -1pt; }
  @page { margin: 0.5in; }
  @media print { body { margin: 0; } }
`;

const header = (title, quote) => `
  <div class="strip"></div>
  <h1>${esc(title)}</h1>
  <div class="meta"><span>National Media Services, Inc. · 613 N. Commerce Ave, Front Royal, VA 22630 · (540) 635-4181</span>
  <span>Issued ${new Date().toISOString().slice(0, 10)}${quote?.expiresOn ? ` · Valid until <b>${esc(quote.expiresOn)}</b>` : ""}</span></div>`;

function openDoc(title, body) {
  const w = window.open("", "_blank", "width=850,height=1000");
  if (!w) {
    alert("Allow pop-ups for this site to print.");
    return;
  }
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title><style>${PAGE_CSS}</style></head><body>${body}</body></html>`);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 300);
}

const contactBlock = (c) => `
  <table><tr><th style="width:25%">Customer</th><td>${esc(c.name) || "&nbsp;"}</td></tr>
  <tr><th>Email</th><td>${esc(c.email) || "&nbsp;"}</td></tr>
  <tr><th>Phone</th><td>${esc(c.phone) || "&nbsp;"}</td></tr></table>`;

export function printGarmentQuote(quote, contact) {
  const rows = quote.lines.flatMap((l) =>
    l.sizeGroups.map((g) => `<tr><td>${esc(l.label)}${l.kind === "roster" ? " (roster)" : ""}</td><td>${esc(g.label)}</td><td class="n">${g.qty}</td><td class="n">${usd(g.unit)}</td><td class="n">${usd(g.total)}</td></tr>`),
  );
  const extra = [
    ...quote.orderFees.map((f) => [f.label, f.amount]),
    ...(quote.rush.amount ? [[quote.rush.label, quote.rush.amount]] : []),
    ...(quote.delivery ? [[quote.delivery.label, quote.delivery.amount]] : []),
  ];
  openDoc("NMS Quote", `
    ${header("Apparel Quote", quote)}
    <h2>Customer</h2>${contactBlock(contact)}
    <h2>Items</h2>
    <table><thead><tr><th>Item</th><th>Size</th><th class="n">Qty</th><th class="n">Each</th><th class="n">Amount</th></tr></thead>
    <tbody>${rows.join("")}${extra.map(([k, v]) => `<tr><td colspan="4">${esc(k)}</td><td class="n">${v == null ? "TBD" : usd(v)}</td></tr>`).join("")}</tbody>
    <tfoot><tr><td colspan="4">Total${quote.totalExcludesShipping ? " (shipping calculated separately)" : ""}, before tax</td><td class="n">${usd(quote.total)}</td></tr></tfoot></table>
    <p class="box">Quotes are valid for 14 days due to supplier inventory and pricing fluctuations. Production will not commence until payment${quote.lines.some((l) => /Customer-supplied/.test(l.label)) ? " and signed waivers are" : " is"} received.</p>`);
}

export function printWaiver(quote, contact) {
  const garments = quote.lines.filter((l) => /Customer-supplied/.test(l.label));
  openDoc("Customer-Supplied Garment Waiver", `
    ${header("Customer-Supplied Garment Waiver", quote)}
    <h2>Order &amp; customer details</h2>${contactBlock(contact)}
    <table style="margin-top:8pt"><thead><tr><th>Garment description</th><th class="n">Qty</th></tr></thead>
    <tbody>${garments.map((l) => `<tr><td>${l.sizeGroups.map((g) => `${esc(g.label)} × ${g.qty}`).join(", ")}<br>&nbsp;</td><td class="n">${l.qty}</td></tr>`).join("")}</tbody></table>
    <p><span class="cb"></span>I acknowledge the <b>$5.00 per garment</b> customer garment handling fee.</p>

    <h2>Fulfillment &amp; delivery</h2>
    <p><span class="cb"></span>Bulk box (free) &nbsp;&nbsp; <span class="cb"></span>White-glove kitting &amp; bagging (+$1.00/garment)</p>
    <p><span class="cb"></span>Will-call / studio pickup &nbsp;&nbsp; <span class="cb"></span>Local courier ($20.00 minimum) &nbsp;&nbsp; <span class="cb"></span>Standard shipping (carrier rate)</p>

    <h2>Intellectual property &amp; copyright</h2>
    <p class="box">I confirm that I either own the intellectual property rights to the design(s) for this order, or I have received explicit, documented permission from the copyright/trademark owner to reproduce and distribute them. I agree to hold National Media Services, Inc. fully harmless from any legal claims, damages, or liabilities arising from copyright or trademark infringement related to the printing of this order.</p>

    <h2>Customer-supplied garment damage liability release</h2>
    <p class="box">By signing below, I acknowledge and agree that National Media Services, Inc. is handling customer-supplied merchandise at my direct request. I understand that heat-press applications (Direct-to-Film) carry inherent risks of fabric scorching, melting, seam distortion, or material failure due to unknown fabric blends, pre-existing wear, or chemical treatments. I hereby release National Media Services, Inc., its staff, and its owners from any financial liability, replacement costs, or claims for garments damaged during the normal course of heat-press printing.</p>

    <h2>Payment terms &amp; quote expiration</h2>
    <p>This quote is valid until <b>${esc(quote.expiresOn ?? "")}</b> (14 days) due to supplier inventory and pricing fluctuations. Production will not commence until payment and this signed waiver are received.</p>

    <div class="lines"><div class="sig">Customer signature</div><div class="sig">Date</div><div class="sig">Printed name</div><div class="sig">NMS staff</div></div>`);
}
